import { contractAddress, Liquidity } from "@verified-network/verified-sdk";
import { ethers } from "ethers";
import { useEffect, useState } from "react";
import ERC20ABI from "../utils/abis/ERC20.json";
import { currenciesToSymbol } from "../utils/constants";
import { ChainConfig, chainDetails } from "../utils/config";
import useNetworkStore from "../store/networkStore";
import useUserStore from "../store/userSlice";

export const useGetSupportedCurrencies = (action) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { chainId, provider, web3, signer } = useNetworkStore();
  const { userAddress } = useUserStore();

  useEffect(() => {
    const fetchAndFormatSupportedCurrencies = async () => {
      setLoading(true);
      setError(null);
      const liquidityContract = new Liquidity(
        signer,
        contractAddress[chainId].Liquidity,
      );
      if (liquidityContract) {
        try {
          const response = await liquidityContract
            .getSupportedTokens()
            .then((res) => res)
            .catch((err) => null);
          const suppportedTokens = response?.response?.result || [];
          const cash = contractAddress[chainId].CASH;
          const cashTokens = Object.keys(cash).map((ky) => {
            if (cash[ky]?.length > 0) {
              return {
                tokenName: ky,
                tokenAddress: cash[ky],
              };
            } else {
              return null;
            }
          });

          const allTokens =
            action === "borrow"
              ? suppportedTokens
              : action === "withdraw-funds"
                ? cashTokens?.filter((tk) => tk !== null)
                : [
                    ...cashTokens?.filter((tk) => tk !== null),
                    ...suppportedTokens,
                  ];
          if (allTokens) {
            const jsonResult = await Promise.all(
              allTokens.map(async (token) => {
                const tokenName = token.tokenName.replace(/\0/g, "");
                const isFiat =
                  tokenName.substring(0, 2) === "VC" ? true : false;
                let currencyBalance,
                  currencyName,
                  currencySymbolFmt,
                  tokenDecimals,
                  tokenBalance;
                if (
                  token?.tokenAddress?.toLowerCase() !==
                  ethers.constants.AddressZero.toLowerCase()
                ) {
                  const currencyContract = new web3.eth.Contract(
                    ERC20ABI,
                    token.tokenAddress,
                  );
                  const currencyNameRaw = await currencyContract.methods
                    .name()
                    .call();
                  const currencySymbolRaw = await currencyContract.methods
                    .symbol()
                    .call();
                  currencySymbolFmt = currencySymbolRaw.replace(/\0/g, "");
                  currencyName = isFiat
                    ? "Verified Cash " +
                      tokenName.substring(2, tokenName.length)
                    : currencyNameRaw.replace(/\0/g, "");
                  const currencyDecimals = await currencyContract.methods
                    .decimals()
                    .call();
                  tokenDecimals = Number(currencyDecimals);
                  const currencyBalanceRaw = await currencyContract.methods
                    .balanceOf(userAddress)
                    .call();
                  tokenBalance = Number(currencyBalanceRaw);
                  currencyBalance = ethers.utils.formatUnits(
                    currencyBalanceRaw.toString(),
                    currencyDecimals,
                  );
                } else {
                  const balance = await web3.eth.getBalance(userAddress);
                  tokenBalance = Number(balance);
                  currencyBalance = web3.utils.fromWei(balance, "ether");
                  currencyName = tokenName;
                  tokenDecimals = Number(
                    ChainConfig[chainId]?.nativeCurrency?.decimals,
                  );
                }
                return {
                  id: token.tokenAddress,
                  tokenAddress: token.tokenAddress,
                  address: token.tokenAddress,
                  tokenDecimals,
                  tokenName,
                  isCryptoFiat: isFiat,
                  isCashToken: cashTokens.find(
                    (csh) =>
                      csh?.tokenAddress?.toLowerCase() ===
                      token.tokenAddress?.toLowerCase(),
                  )
                    ? true
                    : false,
                  balance: currencyBalance,
                  tokenBalance,
                  isCrypto: true,
                  currencySymbol: tokenName,
                  currencySymbolFmt,
                  officialCurrencySymbol: isFiat
                    ? currenciesToSymbol[tokenName.replace("VC", "")]
                    : token?.tokenAddress?.toLowerCase() ===
                        ethers.constants.AddressZero.toLowerCase()
                      ? currenciesToSymbol[tokenName.toLowerCase()]
                      : currenciesToSymbol[currencySymbolFmt?.toUpperCase()]
                        ? currenciesToSymbol[currencySymbolFmt?.toUpperCase()]
                        : currencySymbolFmt,
                  currencyName,
                  symbolFmt: tokenName
                    ?.toString()
                    .substring(2, tokenName?.toString()?.length)
                    .toUpperCase(),
                };
              }),
            );
            if (action === "add-funds") {
              setData(
                jsonResult?.filter(
                  (curr) =>
                    (curr?.isCashToken && curr?.isCryptoFiat) ||
                    curr?.tokenAddress?.toLowerCase() ===
                      chainDetails[chainId]?.usdc?.toLowerCase() ||
                    curr?.tokenAddress?.toLowerCase() ===
                      ethers.constants.AddressZero?.toLowerCase(),
                ),
              );
              return;
            }

            if (action === "add-funds-crypto") {
              setData(
                jsonResult?.filter(
                  (curr) =>
                    curr?.tokenAddress?.toLowerCase() ===
                      chainDetails[chainId]?.usdc?.toLowerCase() ||
                    curr?.tokenAddress?.toLowerCase() ===
                      ethers.constants.AddressZero?.toLowerCase(),
                ),
              );
              return;
            }

            if (action === "withdraw-funds") {
              setData(
                jsonResult?.filter(
                  (curr) => curr?.isCryptoFiat && curr?.isCashToken,
                ),
              );
              return;
            }

            setData(jsonResult);
          }
        } catch (err) {
          console.error("Error while fetching all pools: ", err);
          setError(
            err.message || "Error while fetching all pools. Try again later.",
          );
        } finally {
          setLoading(false);
        }
      }
    };

    if (chainId && provider && web3 && userAddress) {
      fetchAndFormatSupportedCurrencies();
    }
  }, [chainId, provider, web3, userAddress, action, signer]);

  return {
    getSupportedCurrenciesData: data,
    getSupportedCurrenciesLoading: loading,
    getSupportedCurrenciesError: error,
  };
};
